<template>
  <div>
    <vue-headful :title="title" />
    <div class="mx-4 my-2" v-if="terminal">
      <b-notification type="is-warning is-light" :closable="false" v-if="!terminal?.serial && terminal?.name.toLowerCase().includes('test3')">
        This is a raw Test3 terminal straight out of the factory and has not been hydrated either with Test5 serials or by Salesforce.
      </b-notification>
      <b-notification type="is-warning is-light" :closable="false" v-if="terminal?.serial && !hydratedBySalesforce">
        This is a freshly manufactured terminal straight out of the factory and has not been hydrated by Salesforce.
      </b-notification>
      <div class="columns mb-1">
        <div class="column is-one-half">
          <TerminalNetworkStatusComponent />
          <div class="card">
            <div class="card-header">
              <p class="card-header-title">Overview</p>
            </div>
            <div class="card-content">
              <TerminalOverviewComponent />
            </div>
          </div>
        </div>
        <div class="column is-one-half">
          <div class="box m-0 p-0" style="height:100%">
            <remote-detail-map :full-height="true" map-style="streets-v12" :satellite="mapObject.satellite" :cellular="mapObject.cellular" :is-private="terminal.private" 
              v-if="terminal.location?.coordinates?.latitude && terminal.location?.coordinates?.longitude" />
            <div style="height: 100%; width: 100%;" class="is-flex is-justify-content-center is-align-items-center kpt-map-not-found" v-else>
              <span class="box has-text-centered">
                <p><strong>Map not available.</strong></p>
                <p>{{ mapNotAvailableErrorMessage }}</p>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="card">
        <div class="card-header">
          <p class="card-header-title">Hardware</p>
        </div>
        <div class="card-content">
          <TerminalHardwareComponent />
        </div>
      </div>
      <div class="card mt-4">
        <div class="card-header">
          <p class="card-header-title">Software</p>
        </div>
        <div class="card-content">
          <TerminalSoftwareComponent />
        </div>
      </div>
      <div class="card mt-4" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')">
        <div class="card-header">
          <p class="card-header-title">Private Mode</p>
        </div>
        <div class="card-content">
          <div class="is-flex is-align-items-center">
            <b-button :type="{ 'is-primary is-outlined': !terminal.private, 'is-danger': terminal.private }" 
              :disabled="(!terminal?.hardware?.geoModem?.serial)"
              @click="openPrivateModal">
                <span v-if="!terminal.private">Enable</span>
                <span v-else>Disable</span>
              </b-button>
            <b-message type="is-light" class="content ml-4">
              <p>Setting or removing private mode requires modem model and serial to be defined. You must confirm the setting by signing your name.</p>
            </b-message>
          </div>
        </div>
      </div>
      <div class="card my-4">
        <div class="card-header">
          <p class="card-header-title">Subscription</p>
        </div>
        <div class="card-content">
          <div class="columns">
            <div class="column is-four-fifths" v-if="terminal?.activeSubscription">
              <detail-field-2>
                <template v-slot:label>Product</template>
                <template v-slot:value>{{ terminal?.activeSubscription?.description }}</template>
              </detail-field-2>
              <detail-field-2>
                <template v-slot:label>Data Package</template>
                <template v-slot:value>{{ terminal?.activeSubscription?.dataLimitInGb ? terminal?.activeSubscription?.dataLimitInGb === 9999 ? 'Unlimited' : `${terminal?.activeSubscription?.dataLimitInGb}GB` : 'N/A' }}</template>
              </detail-field-2>
              <detail-field-2>
                <template v-slot:label>Start Date</template>
                <template v-slot:value>
                  {{ format(addMinutes(new Date(terminal?.activeSubscription?.startDate), new Date(terminal?.activeSubscription?.startDate).getTimezoneOffset()), 'yyyy-MM-dd HH:mm:ss') }} (UTC)
                </template>
              </detail-field-2>
              <detail-field-2>
                <template v-slot:label>End Date</template>
                <template v-slot:value>
                  {{ format(addMinutes(new Date(terminal?.activeSubscription?.endDate), new Date(terminal?.activeSubscription?.endDate).getTimezoneOffset()), 'yyyy-MM-dd HH:mm:ss') }} (UTC)
                </template>
              </detail-field-2>
            </div>
            <div class="column is-four-fifths" v-else>
              <p>No active subscription found.</p>
            </div>
            <div class="column is-one-fifth">
              <div class="px-4 action-links-container" v-if="$hasRole('Super User,Kymeta Admin,Sales Support,Product Support,Account Admin')">
                <p v-if="terminal?.activeSubscription"><router-link :to="{ name: 'subscription2Overview', params: { id: terminal?.activeSubscription.subscriptionId }}">View Subscription</router-link></p>
                <p class="mt-2" v-if="terminal?.subscriptionHistory"><a @click="openSubscriptionHistoryModal">View Past Subscriptions</a></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <b-loading :is-full-page="false" v-model="terminalLoading" :can-cancel="false"></b-loading>
  </div>
</template>
<script setup>
import { computed, getCurrentInstance } from 'vue';
import { format, addMinutes } from 'date-fns';
import { useTerminalsStore } from '../terminals-store';

import RemoteDetailMap from '../../remotes/components/RemoteDetailMapComponent.vue';

import TerminalSoftwareComponent from '../components/TerminalSoftwareComponent.vue';
import TerminalOverviewComponent from '../components/TerminalOverviewComponent.vue';
import TerminalNetworkStatusComponent from '../components/TerminalNetworkStatusComponent.vue';
import TerminalPrivateConfirmModal from '../components/TerminalPrivateConfirmModal.vue';
import TerminalSubscriptionHistoryModal from '../components/TerminalSubscriptionHistoryModal.vue';
import TerminalHardwareComponent from '../components/TerminalOverviewHardware.vue';

const store = useTerminalsStore();
const app = getCurrentInstance();
const terminal = computed(() => store.state.terminal);
const terminalLoading = computed(() => store.state.terminalLoading);
const title = computed(() => `${terminal.value?.name} / Overview`);

const mapObject = computed(() => ({
  satellite: {
    satelliteLocationUpdatedOn: terminal.value?.networkStatus?.satelliteLastUpdated,
    online: terminal.value?.networkStatus.satelliteOnline,
    latitude: terminal.value?.location?.coordinates?.latitude,
    longitude: terminal.value?.location?.coordinates?.longitude
  },
  cellular: {
    cellularLocationUpdatedOn: terminal.value?.networkStatus?.cellularLastUpdated,
    online: terminal.value?.networkStatus.cellularOnline,
    latitude: terminal.value?.location?.coordinates?.latitude,
    longitude: terminal.value?.location?.coordinates?.longitude
  }
}));

const mapNotAvailableErrorMessage = computed(() => {
  if (!terminal?.value?.location?.coordinates) return 'Coordinates for this terminal are missing.';
  if (terminal.value.location.coordinates.latitude == 0 && terminal.value.location.coordinates.longitude == 0) return 'Coordinates for this terminal are null island (0,0).';
  return '';
});
const hydratedBySalesforce = computed(() => !!(terminal?.value?.hardware?.terminal?.assetId));
const openSubscriptionHistoryModal = () => {
  app.proxy.$buefy.modal.open({
    hasModalCard: true,
    component: TerminalSubscriptionHistoryModal,
    trapFocus: true,
    width: 700,
    props: {
      terminalId: terminal?.value?.id,
      subHistory: terminal?.value?.subscriptionHistory
    }
  });

  app.proxy.$kya.event('terminal', 'openSubscriptionHistoryModal', 'Opened the subscription history mode modal');
}

const openPrivateModal = () => {
  app.proxy.$buefy.modal.open({
    hasModalCard: true,
    component: TerminalPrivateConfirmModal,
    trapFocus: true,
    width: 960,
    props: {
      privateModeIsOn: terminal?.value?.private,
      terminal,
      store
    }
  });

  app.proxy.$kya.event('terminal', 'openPrivateModeConfigurationModal', 'Opened the private mode modal');
}
</script>
<style scoped>
.kpt-map-not-found {
  background-image: url('~@/assets/map-of-the-world.webp'); 
  background-size: cover; 
  background-position: center center;
}
</style>
